import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import * as api from '../../api/mycore/alertsApi';
import * as actions from './alertsActions';
import { getUserLimits } from '../userLimits';

const selectAlertFilters = (state) => state?.mycore?.alerts?.filters || {};

const buildErrorPayload = (error) => {
  // Handle Django REST Framework validation errors
  // The error can come as error.response.data OR directly as error (from axios interceptor)
  const data = error?.response?.data || error;

  if (data) {
    // If it's a string, return it directly
    if (typeof data === 'string') {
      return data;
    }

    // If there's a non_field_errors array, return first message
    if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
      return data.non_field_errors[0];
    }

    // If there's a detail field
    if (data.detail) {
      return data.detail;
    }

    // If it's an array (for non_field errors returned as array directly)
    if (Array.isArray(data)) {
      return data[0];
    }

    // If it's an object with field errors, format them
    if (typeof data === 'object') {
      const errors = Object.entries(data).map(([field, messages]) => {
        const msg = Array.isArray(messages) ? messages[0] : messages;
        return field === 'non_field_errors' ? msg : `${field}: ${msg}`;
      });
      return errors.join(', ');
    }
  }

  // Fallback to original logic
  return error?.detail || error?.message || 'Unknown error';
};

const normalizeListResponse = (response) => {
  if (!response || !response.data) {
    return { data: [], meta: null };
  }

  if (Array.isArray(response.data)) {
    return { data: response.data, meta: null };
  }

  const { results, ...rest } = response.data;
  if (Array.isArray(results)) {
    return { data: results, meta: rest };
  }

  return { data: response.data, meta: null };
};

function* fetchAlerts(action) {
  try {
    const filters = action.payload;
    const response = yield call(api.fetchAlerts, filters);
    const { data, meta } = normalizeListResponse(response);
    yield put(actions.fetchAlertsSuccess(data, meta));
  } catch (error) {
    yield put(actions.fetchAlertsFailure(buildErrorPayload(error)));
  }
}

function* fetchAlertDetail(action) {
  try {
    const { alertId } = action.payload;
    const response = yield call(api.fetchAlertDetail, alertId);
    yield put(actions.fetchAlertDetailSuccess(response.data));
  } catch (error) {
    yield put(actions.fetchAlertDetailFailure(buildErrorPayload(error)));
  }
}

function* resolveAlert(action) {
  try {
    const { alertId } = action.payload;
    const response = yield call(api.resolveAlert, alertId);
    yield put(actions.resolveAlertSuccess(response.data));
    const filters = yield select(selectAlertFilters);
    yield put(actions.fetchAlertsRequest(filters));
    // Refresh user limits to update the counter
    yield put(getUserLimits());
  } catch (error) {
    yield put(actions.resolveAlertFailure(buildErrorPayload(error)));
  }
}

function* dismissAlert(action) {
  try {
    const { alertId } = action.payload;
    const response = yield call(api.dismissAlert, alertId);
    yield put(actions.dismissAlertSuccess(response.data));
    const filters = yield select(selectAlertFilters);
    yield put(actions.fetchAlertsRequest(filters));
    // Refresh user limits to update the counter
    yield put(getUserLimits());
  } catch (error) {
    yield put(actions.dismissAlertFailure(buildErrorPayload(error)));
  }
}

function* createAlert(action) {
  try {
    const response = yield call(api.createAlert, action.payload);
    yield put(actions.createAlertSuccess(response.data));
    const filters = yield select(selectAlertFilters);
    yield put(actions.fetchAlertsRequest(filters));
    // Refresh user limits to update the counter
    yield put(getUserLimits());
  } catch (error) {
    yield put(actions.createAlertFailure(buildErrorPayload(error)));
  }
}

function* activateNextRecurrence(action) {
  try {
    const { alertId } = action.payload;
    const response = yield call(api.activateNextRecurrence, alertId);
    yield put(actions.activateNextRecurrenceSuccess(response.data));
    // Refresh user limits to update the counter
    yield put(getUserLimits());
  } catch (error) {
    yield put(actions.activateNextRecurrenceFailure(buildErrorPayload(error)));
  }
}

function* markAlertRead(action) {
  try {
    const { alertId } = action.payload;
    const response = yield call(api.markAlertRead, alertId);
    yield put(actions.markAlertReadSuccess(response.data));
    const filters = yield select(selectAlertFilters);
    yield put(actions.fetchAlertsRequest(filters));
    // Remove from notified alerts when marked as read
    yield put(actions.removeNotifiedAlert(alertId));
    // Remove from popover alert list when marked as read
    yield put(actions.removeFromPopoverAlertList(alertId));
  } catch (error) {
    yield put(actions.markAlertReadFailure(buildErrorPayload(error)));
  }
}

function* markAllAlertsRead() {
  try {
    const response = yield call(api.markAllAlertsRead);
    yield put(actions.markAllAlertsReadSuccess(response.data));
    const filters = yield select(selectAlertFilters);
    yield put(actions.fetchAlertsRequest(filters));
    // Clear all notified alerts when marking all as read
    yield put({ type: 'CLEAR_NOTIFIED_ALERTS' });
    // Clear popover alert list when marking all as read
    yield put(actions.clearPopoverAlertList());
    // Refresh unread count
    yield put(actions.fetchUnreadCountRequest());
  } catch (error) {
    yield put(actions.markAllAlertsReadFailure(buildErrorPayload(error)));
  }
}

function* fetchUnreadCount() {
  try {
    const response = yield call(api.fetchUnreadCount);
    yield put(actions.fetchUnreadCountSuccess(response.data.count));
  } catch (error) {
    yield put(actions.fetchUnreadCountFailure(buildErrorPayload(error)));
  }
}

function* advanceBatchFromAlert(action) {
  try {
    const { alertId } = action.payload;
    const response = yield call(api.advanceBatchFromAlert, alertId);
    yield put(actions.advanceBatchFromAlertSuccess(response.data));
    // Refresh alerts list and unread count
    const filters = yield select(selectAlertFilters);
    yield put(actions.fetchAlertsRequest(filters));
    yield put(actions.fetchUnreadCountRequest());
  } catch (error) {
    yield put(actions.advanceBatchFromAlertFailure(buildErrorPayload(error)));
  }
}

export default function* alertsSaga() {
  yield all([
    takeLatest(actions.FETCH_ALERTS.REQUEST, fetchAlerts),
    takeLatest(actions.FETCH_ALERT_DETAIL.REQUEST, fetchAlertDetail),
    takeLatest(actions.RESOLVE_ALERT.REQUEST, resolveAlert),
    takeLatest(actions.DISMISS_ALERT.REQUEST, dismissAlert),
    takeLatest(actions.CREATE_ALERT.REQUEST, createAlert),
    takeLatest(actions.ACTIVATE_NEXT_RECURRENCE.REQUEST, activateNextRecurrence),
    takeLatest(actions.MARK_ALERT_READ.REQUEST, markAlertRead),
    takeLatest(actions.MARK_ALL_ALERTS_READ.REQUEST, markAllAlertsRead),
    takeLatest(actions.FETCH_UNREAD_COUNT.REQUEST, fetchUnreadCount),
    takeLatest(actions.ADVANCE_BATCH_FROM_ALERT.REQUEST, advanceBatchFromAlert),
  ]);
}
